import * as React from 'react';
import { assign } from '../../../../../shared/Assign';
import Button from '@mui/material/Button';
import CloseIcon from '@mui/icons-material/Close';
import { DatePicker } from '@mui/x-date-pickers';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';

type SendRequestModalProps = {
    labels: SendRequestModalLabels;
    show: boolean;
    onClose: () => void;
    orderCommandForBooking: any;
    createBookingRequest: any;
};

type SendRequestModalLabels = {
    requestModalTitle: string;
    requestModalText: string;
    requestModalDate: string;
    requestModalInfo: string;
    cancelButton: string;
    sendRequest: string;
};

const SendRequestModal = ({ labels, show, onClose, orderCommandForBooking, createBookingRequest }: SendRequestModalProps) => {
    const [desiredDate, setDesiredDate] = React.useState<Date | null>(null);
    const [otherInfo, setOtherInfo] = React.useState<string>('');
    const [errorDate, setErrorDate] = React.useState<boolean>(false);

    const handleClose = () => {
        setDesiredDate(null);
        setOtherInfo('');
        onClose();
    };

    const handleDesiredTimeChange = (newValue) => {
        setErrorDate(false);
        if (new Date(newValue).toDateString() === 'Invalid Date') {
            setErrorDate(true);
        }
        setDesiredDate(newValue);
    };

    const handleOtherInfoChange = (newValue) => {
        setOtherInfo(newValue);
    };

    const handleSendRequest = () => {
        let newOrderCommandForBooking = assign({}, orderCommandForBooking);
        if (otherInfo) {
            newOrderCommandForBooking.other.notes = otherInfo;
        }
        if (desiredDate) {
            newOrderCommandForBooking.other.preferredTimeOfPhotographing = desiredDate;
        }
        newOrderCommandForBooking.calendarItem = null;
        newOrderCommandForBooking.bookDirectly = false;
        newOrderCommandForBooking.isBookingRequest = true;
        createBookingRequest(newOrderCommandForBooking);
    };

    return (
        <Dialog onClose={handleClose} open={show} aria-label='send-request-modal'>
            <DialogTitle>
                {labels.requestModalTitle}
                <IconButton
                    aria-label="close-send-request-modal"
                    onClick={handleClose}
                    sx={{
                        position: 'absolute',
                        right: 8,
                        top: 8
                    }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent >
                <DialogContentText sx={{
                    fontSize: '16px',
                    padding: '0 0 20px 0'
                }}>
                    {labels.requestModalText}
                </DialogContentText>
                <DatePicker
                    label={labels.requestModalDate}
                    value={desiredDate}
                    inputFormat="yyyy-MM-dd"
                    disablePast
                    onChange={handleDesiredTimeChange}
                    renderInput={(params) => (
                        <TextField fullWidth {...params}
                            sx={{
                                marginBottom: '20px',
                                '& svg': {
                                    zIndex: '1',
                                }
                            }}
                        />
                    )}
                />
                <TextField
                    InputProps={{
                        endAdornment:
                            <IconButton
                                aria-label="delete-text"
                                onClick={() => handleOtherInfoChange('')}
                                sx={{
                                    zIndex: '9999',
                                    margin: '-45px -10px 0 0'
                                }}
                            >
                                <CloseIcon />
                            </IconButton>
                    }}
                    fullWidth
                    multiline
                    minRows={3}
                    value={otherInfo}
                    onChange={(event) => handleOtherInfoChange(event.target.value)}
                    label={labels.requestModalInfo}
                />
            </DialogContent>
            <DialogActions sx={{
                justifyContent: 'space-between',
                padding: '0px 24px 16px 24px'
            }}>
                <Button onClick={handleClose} variant='text'>
                    {labels.cancelButton}
                </Button>
                <Button aria-label="send-request-btn" onClick={handleSendRequest} variant="contained" disabled={errorDate}>
                    {labels.sendRequest}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default SendRequestModal;